import { useCallback, useState } from 'react';
import { apiPost, apiDelete } from '../client';
import { useNotesStore } from '@/store/notesStore';
import type { ApiNote } from './types';
import { mapApiToNote } from './noteUtils';

/**
 * Per-note tag mutations with optimistic store updates.
 *
 * addTag  → POST /app/v1/note/{noteId}/tags
 * removeTag → DELETE /app/v1/note/{noteId}/tags/{tagName}
 *
 * Both calls immediately patch the note in the store (optimistic),
 * then reconcile with the server response.
 */
export function useNoteTags(noteId: string) {
  const updateNote = useNotesStore((s) => s.updateNote);
  const notes = useNotesStore((s) => s.notes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addTag = useCallback(
    async (name: string) => {
      // Optimistic update
      const current = notes.find((n) => n.id === noteId);
      if (current) {
        const optimisticTags = [...(current.tags ?? []), name];
        updateNote(noteId, { tags: optimisticTags });
      }

      setLoading(true);
      setError(null);
      try {
        const response = await apiPost<{ note: ApiNote }>(
          `/app/v1/note/${noteId}/tags`,
          { name }
        );
        const updated = mapApiToNote(response.note);
        updateNote(noteId, { tags: updated.tags });
        return updated.tags ?? [];
      } catch (e: any) {
        // Roll back optimistic update
        if (current) updateNote(noteId, { tags: current.tags ?? [] });
        const message = e?.message || 'Failed to add tag';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [noteId, notes, updateNote]
  );

  const removeTag = useCallback(
    async (name: string) => {
      // Optimistic update
      const current = notes.find((n) => n.id === noteId);
      if (current) {
        const optimisticTags = (current.tags ?? []).filter((t) => t !== name);
        updateNote(noteId, { tags: optimisticTags });
      }

      setLoading(true);
      setError(null);
      try {
        const encodedName = encodeURIComponent(name);
        const response = await apiDelete<{ note: ApiNote }>(
          `/app/v1/note/${noteId}/tags/${encodedName}`
        );
        const updated = mapApiToNote(response.note);
        updateNote(noteId, { tags: updated.tags });
        return updated.tags ?? [];
      } catch (e: any) {
        // Roll back optimistic update
        if (current) updateNote(noteId, { tags: current.tags ?? [] });
        const message = e?.message || 'Failed to remove tag';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [noteId, notes, updateNote]
  );

  return { addTag, removeTag, loading, error };
}

